import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { icons, PageHelpContext } from "../company-ui";
import { Article } from "./Article";
import { ARTICLE_BY_ID, articleForPath } from "./content";

interface HelpApi {
  /** Opens the drawer on an article, or on this page's article. */
  openHelp(id?: string): void;
  closeHelp(): void;
}
const HelpContext = createContext<HelpApi>({ openHelp: () => {}, closeHelp: () => {} });
export const useHelp = () => useContext(HelpContext);

/**
 * Page help (ALN-53): the side drawer from the mockup (`.helpdrawer`). The Help button in every
 * page header and the `?` key open the article whose `route` matches the page. Esc closes it and
 * focus goes back to where it was. Links inside open other articles in the drawer.
 */
export function HelpProvider({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const [id, setId] = useState<string | null>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);

  const openHelp = useCallback((next?: string) => {
    setId(cur => {
      if (cur === null) returnTo.current = document.activeElement as HTMLElement | null;
      return next && ARTICLE_BY_ID[next] ? next : articleForPath(window.location.pathname).id;
    });
  }, []);
  const closeHelp = useCallback(() => {
    setId(cur => {
      if (cur !== null) {
        const back = returnTo.current;
        returnTo.current = null;
        queueMicrotask(() => { if (back && document.contains(back)) back.focus(); });
      }
      return null;
    });
  }, []);

  useEffect(() => { if (id) closeBtn.current?.focus(); }, [id]);
  // A new page closes the drawer (its footer links navigate).
  useEffect(() => { setId(null); returnTo.current = null; }, [location]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "Escape" && id) { e.preventDefault(); closeHelp(); return; }
      const typing = (e.target as HTMLElement | null)?.closest?.("input, textarea, select, [contenteditable]");
      if (e.key === "?" && !typing) { e.preventDefault(); openHelp(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [id, openHelp, closeHelp]);

  const api = useMemo(() => ({ openHelp, closeHelp }), [openHelp, closeHelp]);
  const pageHelp = useCallback(() => openHelp(), [openHelp]);
  const article = id ? ARTICLE_BY_ID[id] : null;
  return (
    <HelpContext.Provider value={api}>
      <PageHelpContext.Provider value={pageHelp}>
        {children}
        {article && (
          <aside className="helpdrawer" role="dialog" aria-modal="false" aria-labelledby="help-drawer-title">
            <div className="helpdrawer__h">
              <h2 id="help-drawer-title">{article.title}</h2>
              <button ref={closeBtn} type="button" className="iconbtn" aria-label="Close help" onClick={closeHelp}>{icons.x}</button>
            </div>
            <div className="helpdrawer__b">
              <Article article={article} level={3} onOpen={openHelp} />
            </div>
            <div className="helpdrawer__f">
              <Link className="btn btn--sm" href={`/help/${article.id}`}>Open in the help center</Link>
              <Link className="btn btn--sm btn--ghost" href="/help">All help topics</Link>
            </div>
          </aside>
        )}
      </PageHelpContext.Provider>
    </HelpContext.Provider>
  );
}
